import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FolderInput, XIcon } from "lucide-react";
import moment from "moment-timezone";
import { Sheet, SheetContent, SheetClose } from "../../../../../../components/ui/sheet";
import Button from "../../../../../../components/shared/Button";
import Select from "../../../../../../components/shared/inputs/Select";
import Content from "./Content";
import { useScheduleContext } from "../../../hooks/context/useScheduleContext";
import { useIsTemplate } from "../../../hooks/useIsTemplate";
import { useFilters } from "../../../../../../utils/hooks/useFilters";
import { useExportScheduleQuery } from "../../../api/useExportScheduleQuery";
import { useCompanyDateFormat } from "../../../../../../utils/hooks/company";

function ListView() {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const [viewBy, setViewBy] = useState("user");
    const { listView, dispatch, events, type, calendarEvents, allResources } = useScheduleContext();
    const isTemplate = useIsTemplate();
    const [filters] = useFilters();
    const exportSchedule = useExportScheduleQuery();

    const data = useMemo(() => {
        let _events = (filters?.mode === "month"
            ? Object.values(calendarEvents || {}).flatMap((item: any) => item?.events || [])
            : events
        )?.filter((event: any) => event?.type !== "unavailable");

        if (viewBy === "date") {
            _events = _events?.sort(
                (a: any, b: any) => moment(a.startDate).valueOf() - moment(b.startDate).valueOf()
            );
        }

        if (listView?.type === "row") {
            _events = _events
                ?.filter((event: any) => event.resourceId === listView?.selected?.id)
                ?.map((event: any) => ({
                    ...event,
                    resource: listView?.selected,
                    user: listView?.selected
                }));
        }

        return _events?.map((event: any) => {
            const resource =
                allResources?.find((res: any) => res.id === event?.resourceId) ||
                allResources?.find((res: any) => res.id === (event?.user?.id || event?.user)) ||
                event?.user;
            return { ...event, resource, user: resource };
        });
    }, [filters?.mode, calendarEvents, events, viewBy, listView?.type, listView?.selected, allResources]);

    const dateRange = useMemo(() => {
        if (filters?.mode === "day") {
            return (
                <div className="tw-text-neutral-700 tw-text-sm">
                    ({moment(filters?.from).format(dateFormat)})
                </div>
            );
        } else {
            const from = filters?.mode === "month" ? moment(filters.from).startOf("week") : filters.from;
            const to = filters?.mode === "month" ? moment(filters.to).endOf("week") : filters.to;
            return (
                <div className="tw-text-neutral-600 tw-font-medium tw-text-sm">
                    ({moment(from).format(dateFormat)} - {moment(to).format(dateFormat)})
                </div>
            );
        }
    }, [filters?.mode, filters.from, filters.to, dateFormat]);

    const close = useCallback(() => {
        dispatch({ type: "list-view" });
        setViewBy("user");
    }, [dispatch]);

    const onExport = useCallback(() => {
        const isMonth = filters?.mode === "month";
        const isPaged = type === "team" || type === "template";
        const baseParams: any = {
            filters: filters?.filters ?? [],
            sort: filters?.sort,
        };

        if (!isTemplate) {
            baseParams.startDate = isMonth
                ? moment(filters?.from).startOf("week").format("YYYY-MM-DD")
                : moment(filters?.from).format("YYYY-MM-DD");

            baseParams.endDate = isMonth
                ? moment(filters?.to).endOf("week").format("YYYY-MM-DD")
                : moment(filters?.to).format("YYYY-MM-DD");
        } else {
            baseParams.mode = filters?.mode;
            baseParams.day = filters?.day;
        }

        if (listView?.type === "row") {
            baseParams.employeeNumber = listView?.selected?.employeeNumber;
        }

        if (isPaged && !isMonth) {
            baseParams.page = Number(filters?.page || 1) - 1;
            baseParams.perPage = filters?.pageSize || 50;
        }
        exportSchedule.mutate(baseParams);
    }, [exportSchedule, filters, type, listView, isTemplate]);

    return (
        <Sheet open={!!listView} onOpenChange={close}>
            <SheetContent
                side="bottom"
                className="[&>button]:tw-hidden data-[side=bottom]:max-h-[50vh] data-[side=top]:max-h-[50vh] tw-max-w-full"
            >
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-p-5 tw-border-b">
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <h1 className="tw-font-semibold tw-text-sm">{t("list-view")}</h1>
                        {dateRange}
                    </div>
                    <SheetClose asChild>
                        <XIcon size={19} className="tw-cursor-pointer" />
                    </SheetClose>
                </div>

                <div className="tw-m-5">
                    <div className="tw-mb-5 tw-flex tw-items-center tw-justify-between">
                        <div className="tw-flex tw-items-center tw-gap-2">
                            <p className="tw-text-sm tw-font-medium tw-text-muted-foreground">{t("view-by-list")}</p>
                            <Select value={viewBy} onChange={setViewBy} className="tw-w-28">
                                <Select.Item value="user">{t("users")}</Select.Item>
                                <Select.Item value="job">{t("job")}</Select.Item>
                                <Select.Item value="location">{t("location")}</Select.Item>
                                <Select.Item value="date">{t("date")}</Select.Item>
                            </Select>
                        </div>

                        <div className="tw-flex tw-items-center tw-gap-2">
                            {/* <Button
                                variant="outline"
                                size="sm"
                                onClick={onExport}
                                disabled={exportSchedule.isPending}
                                loading={exportSchedule.isPending}
                            >
                                <FolderInput />
                                {t("export")}
                            </Button> */}
                        </div>
                    </div>

                    <Content viewBy={viewBy} data={data} />
                </div>
            </SheetContent>
        </Sheet>
    );
}

export default ListView;
